import { useState } from 'react';
import { useCreateUser } from '../../api/admin';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../lib/api';
import type { UserRole } from '../../types/api';

const ROLES: { value: UserRole; label: string; hint: string }[] = [
  { value: 'student', label: 'Student', hint: 'Books lessons' },
  { value: 'tutor', label: 'Tutor', hint: 'Publishes availability and teaches' },
  { value: 'admin', label: 'Administrator', hint: 'Full access to this console' },
];

export function CreateUserModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const createUser = useCreateUser();
  const { notify } = useToast();

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>('student');
  const [phone, setPhone] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [errors, setErrors] = useState<string[]>([]);

  const reset = () => {
    setEmail('');
    setFullName('');
    setPassword('');
    setRole('student');
    setPhone('');
    setHourlyRate('');
    setErrors([]);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    try {
      const created = await createUser.mutateAsync({
        email: email.trim(),
        fullName: fullName.trim(),
        password,
        role,
        phone: phone.trim() || undefined,
        // The API takes minor units; the operator types whole currency.
        hourlyRateCents:
          role === 'tutor' && hourlyRate
            ? Math.round(Number(hourlyRate) * 100)
            : undefined,
      });
      notify(`Created ${created.fullName}`, 'success');
      close();
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors(err.details.length ? err.details : [err.message]);
      } else {
        setErrors(['Could not create this account.']);
      }
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Add a person"
      description="They can sign in immediately with the password you set here."
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={close}>
            Cancel
          </button>
          <button
            type="submit"
            form="create-user"
            className="btn btn-primary"
            disabled={createUser.isPending}
          >
            {createUser.isPending ? 'Creating…' : 'Create person'}
          </button>
        </>
      }
    >
      <form id="create-user" onSubmit={submit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="label">Full name</span>
            <input
              className="input"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Sara Mahmoud"
            />
          </label>
          <label className="block">
            <span className="label">Email</span>
            <input
              className="input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="sara@example.com"
            />
          </label>
        </div>

        <fieldset>
          <legend className="label">Role</legend>
          <div className="grid gap-2 sm:grid-cols-3">
            {ROLES.map((r) => (
              <label
                key={r.value}
                className={`cursor-pointer rounded-md border px-3 py-2 transition-colors ${
                  role === r.value
                    ? 'border-accent bg-accent-wash'
                    : 'border-rule-strong hover:bg-surface-hover'
                }`}
              >
                <input
                  type="radio"
                  name="role"
                  className="sr-only"
                  checked={role === r.value}
                  onChange={() => setRole(r.value)}
                />
                <span className="block text-xs font-medium text-ink">{r.label}</span>
                <span className="mt-0.5 block text-[0.6875rem] text-ink-3">{r.hint}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="label">Password</span>
            <input
              className="input"
              type="text"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
            />
          </label>
          <label className="block">
            <span className="label">Phone (optional)</span>
            <input
              className="input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+20 123 456 7890"
            />
          </label>
        </div>

        {role === 'tutor' && (
          <label className="block">
            <span className="label">Hourly rate (optional)</span>
            <input
              className="input"
              type="number"
              min="0"
              step="0.01"
              value={hourlyRate}
              onChange={(e) => setHourlyRate(e.target.value)}
              placeholder="250.00"
            />
            <span className="mt-1.5 block text-[0.6875rem] text-ink-3">
              The tutor can change this themselves later.
            </span>
          </label>
        )}

        {errors.length > 0 && (
          <ul
            role="alert"
            className="space-y-1 rounded-md border border-danger/30 bg-danger-wash px-3 py-2 text-xs text-ink"
          >
            {errors.map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
          </ul>
        )}
      </form>
    </Modal>
  );
}
